/**
 * Color parsing shared by the world (normalizing authored colors) and the
 * editor (contrast checks). Hex strings here are *input formats*, never literals.
 */

/** An sRGB color with channels in 0..1, the canonical stored form of a `color` prop. */
export type Rgb = readonly [number, number, number];

/** Parses `#RGB`, `#RRGGBB` or `#RRGGBBAA` into 0–255 channels (alpha ignored). Returns null if malformed. */
export function parseHexBytes(color: string): [number, number, number] | null {
  const hex = color.trim().replace(/^#/, "");
  const full = hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex.slice(0, 6);
  if (
    !/^[0-9a-fA-F]{6}$/.test(full) ||
    (hex.length !== 3 && hex.length !== 6 && hex.length !== 8)
  ) {
    return null;
  }
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as [number, number, number];
}

/** Parses a hex color into 0..1 channels. Returns null if malformed. */
export function parseHexRgb(color: string): Rgb | null {
  const bytes = parseHexBytes(color);
  return bytes ? [bytes[0] / 255, bytes[1] / 255, bytes[2] / 255] : null;
}

/** Formats 0..1 channels as `#rrggbb`, the format native color inputs use. Channels are clamped. */
export function rgbToHex(rgb: Rgb): string {
  const byte = (channel: number) =>
    Math.round(Math.min(1, Math.max(0, channel)) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${rgb.map(byte).join("")}`;
}
